/*
 * Copyright (c) 2023 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { MatStepperModule } from "@angular/material/stepper";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { RouterModule } from "@angular/router";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatDialogModule } from "@angular/material/dialog";
import { MatCardModule } from "@angular/material/card";
import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from "@angular/material/form-field";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from "@angular/common/http";
import { MatButtonModule } from "@angular/material/button";

import { AnvilorNotificationsModule } from "@anvilor/ngx-apps-core";
import { AnvilorCommonModule } from "@anvilor/ngx-apps-core";
import { AnvilorCollectionsModule } from "@anvilor/ngx-apps-core";
import { AnvilorMediaModule } from "@anvilor/ngx-apps-core";
import { NgxThemingModule } from "@anvilor/ngx-theming";
import { NgxRuiLegacyModule, NgxRuiModule } from "@anvilor/ngx-rui";
import { RuiStore } from "@anvilor/ngx-rui";

import { ENVIRONMENT_CONFIG } from "../environments/environment-config";
import { environment } from "../environments/environment";
import { AppComponent } from "./components/app.component";
import { DebugModeToolsComponent } from "./components/debug-mode-tools/debug-mode-tools.component";
import { SubmitButtonControlComponent } from "./components/rui-controls/submit-button-control/submit-button-control.component";
import { SURVEY_RUI_CONTROLS } from "./components/rui-controls";
import { SURVEY_RUI_COMPOSITE_CONTROLS } from "./components/rui-controls";
import { FormLayoutControlComponent } from "./components/rui-controls/form-layout-control/form-layout-control.component";
import { SurveyStatesControlComponent } from "./components/rui-controls/survey-states-control/survey-states-control.component";
import { APP_GOOGLE_MAPS_API_KEY } from "../keys/keys";
import { AttentionBannerControlComponent } from "./components/rui-controls/attention-banner-control/attention-banner-control.component";
import { AttentionBannerComponent } from "./components/attention-banner/attention-banner.component";
import { NGX_THEMING_CONFIG } from "./config/theme-config";
import { SurveyFooterControlComponent } from "./components/survey-footer-control/survey-footer-control";
import { RecaptchaChallengeControlComponent } from "./components/rui-controls/recaptcha-challenge-control/recaptcha-challenge-control.component";
import { GRECAPTCHA_SITE_KEY } from "./services/google-recaptcha.service";
import { APP_GRECAPTCHA_SITE_KEY } from "../keys/keys";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { ReactiveFormsModule } from "@angular/forms";
import { StoreModule } from "@ngrx/store";
import { EffectsModule } from "@ngrx/effects";

@NgModule({
  declarations: [
    AppComponent,
    AttentionBannerControlComponent,
    DebugModeToolsComponent,
    SubmitButtonControlComponent,
    FormLayoutControlComponent,
    SurveyStatesControlComponent,
    SurveyFooterControlComponent,
    AttentionBannerComponent,
    RecaptchaChallengeControlComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    MatButtonModule,
    AnvilorCommonModule,
    NgxRuiModule.forRoot(),
    NgxRuiLegacyModule.forRoot({
      additionalControls: SURVEY_RUI_CONTROLS,
      additionalCompositeControls: SURVEY_RUI_COMPOSITE_CONTROLS,
      googleMapsApiKey: APP_GOOGLE_MAPS_API_KEY,
    }),
    StoreModule.forRoot(),
    EffectsModule.forRoot(),
    NgxThemingModule.forRoot(NGX_THEMING_CONFIG),
    RouterModule.forRoot([{ path: "**", component: AppComponent }]),
    AnvilorNotificationsModule.forRoot(),
    AnvilorMediaModule.forRoot(),
    AnvilorCollectionsModule.forRoot({ mainCollectionsApiBase: environment.apiBase }),
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDialogModule,
    MatCardModule,
    MatStepperModule,
    MatIconModule,
    MatSlideToggle,
    ReactiveFormsModule,
  ],
  providers: [
    RuiStore,
    {
      provide: ENVIRONMENT_CONFIG,
      useValue: environment,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: "fill",
      },
    },
    {
      provide: GRECAPTCHA_SITE_KEY,
      useValue: APP_GRECAPTCHA_SITE_KEY,
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
})
export class AppModule {}
